import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Save, Package, Plus, ShoppingBag, Sparkles, Trash2,
} from "lucide-react";
import { PILL, Field, Input, TextareaField } from "./shared";

// Three opinionated sample products to seed an empty Products tab — kept simple
// so the user can immediately see how products look and tweak/delete the rest.
const SAMPLES = [
  { kind: "service", name: "Consultation Call (30 min)", description: "A focused 30-minute call to scope out your needs and plan the next step together.", price_cents: 5000 },
  { kind: "product", name: "Starter Package", description: "Our most popular package — perfect for first-time customers ready to get started.", price_cents: 14900 },
  { kind: "subscription", name: "Monthly Membership", description: "Recurring access to exclusive offers, support, and new content every month.", price_cents: 2900, recurring_interval: "month" },
];

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | product obj
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/ecommerce/products"); setProducts(data.products || []); }
    catch { toast.error("Couldn't load products"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/ecommerce/products/${p.product_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };

  const seedSamples = async () => {
    setSeeding(true);
    let created = 0;
    for (const s of SAMPLES) {
      const payload = { ...s, currency: "usd", active: true, image_url: "" };
      if (s.kind !== "subscription") delete payload.recurring_interval;
      try { await api.post("/ecommerce/products", payload); created += 1; }
      catch (e) { /* keep going — partial seed is still useful */ }
    }
    setSeeding(false);
    if (created > 0) {
      toast.success(`Added ${created} sample product${created === 1 ? "" : "s"}`);
      load();
    } else {
      toast.error("Couldn't add samples — try adding one manually");
    }
  };

  const fmt = (cents, cur) =>
    `${(cents / 100).toLocaleString("en-US", { style: "currency", currency: (cur || "usd").toUpperCase() })}`;

  if (editing) {
    return <ProductForm initial={editing === "new" ? null : editing} onClose={() => { setEditing(null); load(); }} />;
  }
  if (loading) return <div className="nb-card-flat p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div data-testid="products-tab">
      <div className="nb-card p-5 mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl font-extrabold flex items-center gap-2"><Package className="w-6 h-6" /> Products & Services</div>
          <div className="text-sm text-gray-600 mt-1">Sell products, services, or subscriptions on your website.</div>
        </div>
        <button onClick={() => setEditing("new")} className={`${PILL} bg-[#8B5CF6] text-white`} data-testid="add-product-btn">
          <Plus className="w-4 h-4" /> Add product
        </button>
      </div>
      {products.length === 0 ? (
        <div className="nb-card-flat p-10 text-center">
          <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <div className="font-display text-xl font-extrabold mb-1">No products yet</div>
          <p className="text-sm text-gray-600 mb-4">Add your first product, service, or subscription — or seed a few samples to see how they look.</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button onClick={() => setEditing("new")} className={`${PILL} bg-[#8B5CF6] text-white`} data-testid="add-first-product-btn">
              <Plus className="w-4 h-4" /> Add your first product
            </button>
            <button onClick={seedSamples} disabled={seeding} className={`${PILL} bg-white`} data-testid="seed-sample-products-btn">
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Try sample products
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-3">Samples are fully editable — delete what you don&apos;t need.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="products-list">
          {products.map((p) => (
            <div key={p.product_id} className="nb-card p-4" data-testid={`product-${p.product_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#8B5CF6]">
                  {p.kind === "subscription" ? `Subscription · ${p.recurring_interval || "month"}ly` : p.kind}
                </div>
                {!p.active && <span className="text-[10px] font-extrabold bg-gray-700 text-white px-1.5 py-0.5 rounded">INACTIVE</span>}
              </div>
              <div className="font-display text-lg font-extrabold leading-tight">{p.name}</div>
              {p.description && <div className="text-sm text-gray-600 mt-1 line-clamp-2">{p.description}</div>}
              <div className="text-2xl font-extrabold mt-3">{fmt(p.price_cents, p.currency)}{p.kind === "subscription" && <span className="text-sm text-gray-500 font-bold">/{p.recurring_interval || "month"}</span>}</div>
              <div className="mt-3 flex items-center gap-2">
                <button onClick={() => setEditing(p)} className={`${PILL} bg-white text-sm`} data-testid={`edit-product-${p.product_id}`}><Sparkles className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => del(p)} className={`${PILL} bg-white text-sm`} data-testid={`delete-product-${p.product_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductForm({ initial, onClose }) {
  const [form, setForm] = useState(initial || {
    name: "", description: "", kind: "product", price_cents: 1000, currency: "usd",
    image_url: "", active: true, featured: false, category: "", recurring_interval: "month",
  });
  const [saving, setSaving] = useState(false);
  const onPrice = (v) => setForm({ ...form, price_cents: Math.max(0, Math.round(parseFloat(v || 0) * 100)) });

  const save = async (e) => {
    e?.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      if (form.kind !== "subscription") delete payload.recurring_interval;
      if (initial) await api.put(`/ecommerce/products/${initial.product_id}`, payload);
      else await api.post("/ecommerce/products", payload);
      toast.success(initial ? "Saved" : "Product added");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    setSaving(false);
  };

  return (
    <div data-testid="product-form">
      <div className="nb-card p-5 mb-6 flex items-center justify-between">
        <div>
          <button onClick={onClose} className="text-sm font-semibold flex items-center gap-1 mb-1 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="font-display text-2xl font-extrabold">{initial ? `Edit: ${initial.name}` : "Add product"}</div>
        </div>
      </div>
      <form onSubmit={save} className="nb-card p-5 space-y-4 max-w-2xl">
        <Field label="Type">
          <div className="flex gap-2 flex-wrap">
            {[{ id: "product", label: "Product" }, { id: "service", label: "Service" }, { id: "subscription", label: "Subscription" }].map((k) => (
              <button key={k.id} type="button" onClick={() => setForm({ ...form, kind: k.id })}
                className={`px-4 py-2 rounded-xl border-2 border-black font-bold ${form.kind === k.id ? "bg-[#111] text-white" : "bg-white"}`}
                data-testid={`kind-${k.id}`}>
                {k.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Name">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required testId="product-name" />
        </Field>
        <Field label="Description">
          <TextareaField value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} testId="product-description" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Price (USD)">
            <Input type="number" step="0.01" min="0.5" value={(form.price_cents / 100).toFixed(2)} onChange={(e) => onPrice(e.target.value)} required testId="product-price" />
          </Field>
          {form.kind === "subscription" && (
            <Field label="Billing interval">
              <select value={form.recurring_interval || "month"} onChange={(e) => setForm({ ...form, recurring_interval: e.target.value })}
                data-testid="product-interval"
                className="w-full px-4 py-3 border-2 border-black rounded-xl bg-white font-semibold">
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </select>
            </Field>
          )}
        </div>
        <Field label="Image URL (optional)">
          <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" testId="product-image" />
        </Field>
        <Field label="Category (optional — used by Product Grid section filters)">
          <Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. candles, coffee, apparel" testId="product-category" />
        </Field>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="product-active" />
            Active (visible on your website)
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="product-featured" />
            Featured (show first in &ldquo;Featured&rdquo; grids)
          </label>
        </div>
        <button disabled={saving} className={`${PILL} bg-[#10B981] text-white`} data-testid="save-product-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}
        </button>
      </form>
    </div>
  );
}
